/**
 * Redux reducer for the Preferences component
 * Manages preferences state including modal visibility and user settings
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Interface for the preferences state
 */
export interface PreferencesState {
  isVisible: boolean;
  isDarkMode: boolean;
  profession?: string;
}

/**
 * Initial state for the preferences reducer
 * Modal is initially hidden with default preferences
 */
const initialState: PreferencesState = {
  isVisible: false,
  isDarkMode: false,
  profession: undefined,
};

/**
 * Redux slice for preferences state management
 * Provides reducers for controlling the preferences modal and settings
 */
export const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    /**
     * Shows the preferences modal
     *
     * @param state - Current preferences state
     */
    showPreferences: (state) => {
      state.isVisible = true;
    },
    /**
     * Hides the preferences modal
     *
     * @param state - Current preferences state
     */
    hidePreferences: (state) => {
      state.isVisible = false;
    },
    /**
     * Toggles between light and dark mode
     *
     * @param state - Current preferences state
     */
    toggleDarkMode: (state) => {
      state.isDarkMode = !state.isDarkMode;
    },
    /**
     * Sets the user's profession
     *
     * @param state - Current preferences state
     * @param action - Action with profession string payload
     */
    setProfession: (state, action: PayloadAction<string>) => {
      state.profession = action.payload;
    },
  },
});

/**
 * Action creators generated from the preferences slice
 */
export const { showPreferences, hidePreferences, toggleDarkMode, setProfession } = preferencesSlice.actions;

export default preferencesSlice.reducer;
